import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  CreditCard, 
  Calendar, 
  DollarSign,
  Download,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  History,
  Zap,
  CalendarClock
} from 'lucide-react';
import axios from 'axios';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'text-green-600 bg-green-100';
    case 'pending':
      return 'text-yellow-600 bg-yellow-100';
    case 'failed':
    case 'declined':
      return 'text-red-600 bg-red-100';
    case 'processing':
      return 'text-blue-600 bg-blue-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckCircle size={16} className="text-green-600" />;
    case 'pending':
      return <Clock size={16} className="text-yellow-600" />;
    case 'failed':
    case 'declined':
      return <XCircle size={16} className="text-red-600" />;
    case 'processing':
      return <AlertTriangle size={16} className="text-blue-600" />;
    default:
      return <Clock size={16} className="text-gray-600" />;
  }
};

const ClientProfile = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Helper function to get all scheduled payments from transactions
  // Get all cleared/completed payments (both charge now and scheduled)
  const getClearedPayments = () => {
    const allClearedPayments = [];
    transactions.forEach(transaction => {
      // Add completed charge now payments
      if (transaction.chargeNow && transaction.chargeNow.length > 0) {
        transaction.chargeNow.forEach(payment => {
          if (payment.status === 'completed') {
            allClearedPayments.push({
              ...payment,
              _id: payment._id,
              transactionId: transaction.transactionId,
              paymentType: transaction.paymentType,
              clientName: transaction.client.name,
              type: 'immediate',
              date: payment.processedAt || transaction.createdAt,
              cardLast4: payment.cardLast4,
              cardType: payment.cardType,
              cardholderName: payment.cardholderName,
              paymentMethod: payment.paymentMethod
            });
          }
        });
      }
      // Add completed scheduled payments
      if (transaction.scheduledPayments && transaction.scheduledPayments.length > 0) {
        transaction.scheduledPayments.forEach(payment => {
          if (payment.status === 'completed') {
            allClearedPayments.push({
              ...payment,
              _id: payment._id,
              transactionId: transaction.transactionId,
              paymentType: transaction.paymentType,
              clientName: transaction.client.name,
              type: 'scheduled',
              cardLast4: payment.cardLast4,
              cardType: payment.cardType,
              cardholderName: payment.cardholderName,
              paymentMethod: payment.paymentMethod
            });
          }
        });
      }
    });
    
    // Sort by date, most recent first
    return allClearedPayments.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Get scheduled/pending payments
  const getScheduledPayments = () => {
    const allScheduledPayments = [];
    transactions.forEach(transaction => {
      if (transaction.scheduledPayments && transaction.scheduledPayments.length > 0) {
        transaction.scheduledPayments.forEach(payment => {
          if (payment.status === 'scheduled' || payment.status === 'processing') {
            allScheduledPayments.push({
              ...payment,
              transactionId: transaction.transactionId,
              paymentType: transaction.paymentType,
              clientName: transaction.client.name,
              type: 'scheduled'
            });
          }
        });
      }
    });
    
    // Sort by date, earliest first
    return allScheduledPayments.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  useEffect(() => {
    if (clientId) {
      loadClientProfile();
    }
  }, [clientId]);

  const loadClientProfile = async () => {
    try {
      setLoading(true);
      
      // Load client data, transactions, and saved cards
      const [clientResponse, transactionsResponse, cardsResponse] = await Promise.all([
        axios.get(`/api/clients/${clientId}`),
        axios.get(`/api/clients/${clientId}/transactions`),
        axios.get(`/api/clients/${clientId}/cards`)
      ]);

      setClient(clientResponse.data.client);
      setTransactions(transactionsResponse.data.transactions || []);
      setSavedCards(cardsResponse.data.cards || []);
    } catch (error) {
      console.error('Error loading client profile:', error);
      setError('Failed to load client profile');
    } finally {
      setLoading(false);
    }
  };

  const downloadReceipt = async (transactionId, paymentId) => {
    try {
      const response = await axios.get(
        `/api/payments/receipt/${transactionId}/${paymentId}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${transactionId}-${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const downloadPaymentPlan = async (transactionId) => {
    try {
      const response = await axios.get(
        `/api/payments/payment-plan/${transactionId}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaymentPlan_${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading payment plan:', error);
      if (error.response) {
        alert(`Failed to download payment plan: ${error.response.data?.message || error.response.statusText}`);
      } else {
        alert('Failed to download payment plan. Please check your connection and try again.');
      }
    }
  };

  const downloadAllReceipts = async (transactionId) => {
    try {
      const response = await axios.get(
        `/api/payments/transaction-receipts/${transactionId}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transaction-receipts-${transactionId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading receipts:', error);
      alert('Failed to download receipts. Please try again.');
    }
  };

  const editScheduledPayment = async (transactionId, paymentId) => {
    try {
      // For now, we'll use a simple prompt-based editor
      // In a production app, you'd want a proper modal with form validation
      const payment = client.scheduledPayments.find(p => 
        p.transactionId === transactionId && (p._id === paymentId || p.id === paymentId)
      );
      
      if (!payment) {
        alert('Payment not found');
        return;
      }
      
      const newAmount = prompt('Enter new payment amount:', payment.amount);
      if (newAmount === null) return; // User cancelled
      
      const amount = parseFloat(newAmount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      
      const newDate = prompt('Enter new date (YYYY-MM-DD):', 
        new Date(payment.date).toISOString().split('T')[0]);
      if (newDate === null) return; // User cancelled
      
      const response = await axios.put(
        `/api/payments/scheduled-payment/${transactionId}/${paymentId}`,
        { amount, date: newDate }
      );
      
      if (response.data.success) {
        alert('Scheduled payment updated successfully');
        // Reload the client profile to show updated data
        loadClientProfile();
      } else {
        alert('Failed to update scheduled payment');
      }
    } catch (error) {
      console.error('Error editing scheduled payment:', error);
      alert(error.response?.data?.error || 'Failed to update scheduled payment');
    }
  };

  const deleteScheduledPayment = async (transactionId, paymentId) => {
    try {
      const payment = client.scheduledPayments.find(p => 
        p.transactionId === transactionId && (p._id === paymentId || p.id === paymentId)
      );
      
      if (!payment) {
        alert('Payment not found');
        return;
      }
      
      const confirmed = window.confirm(
        `Are you sure you want to delete this scheduled payment of $${payment.amount}?`
      );
      
      if (!confirmed) return;
      
      const response = await axios.delete(
        `/api/payments/scheduled-payment/${transactionId}/${paymentId}`
      );
      
      if (response.data.success) {
        alert('Scheduled payment deleted successfully');
        // Reload the client profile to show updated data
        loadClientProfile();
      } else {
        alert('Failed to delete scheduled payment');
      }
    } catch (error) {
      console.error('Error deleting scheduled payment:', error);
      alert(error.response?.data?.error || 'Failed to delete scheduled payment');
    }
  };

  const chargeNowScheduledPayment = async (transactionId, paymentId) => {
    try {
      const payment = client.scheduledPayments.find(p => 
        p.transactionId === transactionId && (p._id === paymentId || p.id === paymentId)
      );
      
      if (!payment) {
        alert('Payment not found');
        return;
      }
      
      const confirmed = window.confirm(
        `Charge $${payment.amount} now for this scheduled payment?`
      );
      
      if (!confirmed) return;
      
      const response = await axios.post(
        `/api/payments/charge-now/${transactionId}/${paymentId}`
      );
      
      if (response.data.success) {
        alert('Payment processing initiated. Check back shortly for updated status.');
        // Reload the client profile to show updated data
        loadClientProfile();
      } else {
        alert('Failed to process payment');
      }
    } catch (error) {
      console.error('Error charging payment now:', error);
      alert(error.response?.data?.error || 'Failed to process payment');
    }
  };

  const reschedulePayment = async (transactionId, paymentId) => {
    try {
      const payment = client.scheduledPayments.find(p => 
        p.transactionId === transactionId && (p._id === paymentId || p.id === paymentId)
      );
      
      if (!payment) {
        alert('Payment not found');
        return;
      }
      
      const newDate = prompt('Enter new date (YYYY-MM-DD):', 
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]); // Default to 7 days from now
      if (newDate === null) return; // User cancelled
      
      const response = await axios.put(
        `/api/payments/scheduled-payment/${transactionId}/${paymentId}`,
        { date: newDate }
      );
      
      if (response.data.success) {
        alert('Payment rescheduled successfully');
        // Reload the client profile to show updated data
        loadClientProfile();
      } else {
        alert('Failed to reschedule payment');
      }
    } catch (error) {
      console.error('Error rescheduling payment:', error);
      alert(error.response?.data?.error || 'Failed to reschedule payment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading client profile...</p>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <AlertTriangle size={48} className="text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">Error Loading Profile</h2>
          <p className="text-gray-600 mb-4">{error || 'Client not found'}</p>
          <button
            onClick={() => navigate('/agent')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Calculate summary statistics
  const totalTransactions = transactions.length;
  const totalPaid = transactions.reduce((sum, tx) => sum + (tx.totalPaid || 0), 0);
  const totalPending = transactions.reduce((sum, tx) => {
    return sum + (tx.totalChargeNow + tx.totalScheduled - (tx.totalPaid || 0));
  }, 0);
  const declinedPayments = transactions.reduce((sum, tx) => sum + (tx.totalDeclined || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/agent')}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to Dashboard
              </button>
              <div className="flex items-center">
                <div className="bg-blue-600 p-3 rounded-lg mr-4">
                  <User size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                  <p className="text-gray-600">Client Profile</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Client Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Contact Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <Mail size={20} className="text-blue-600 mr-2" />
              <h3 className="font-medium text-gray-900">Contact</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <Mail size={16} className="text-gray-400 mr-2" />
                <span className="text-gray-600">{client.email}</span>
              </div>
              {client.phone && (
                <div className="flex items-center">
                  <Phone size={16} className="text-gray-400 mr-2" />
                  <span className="text-gray-600">{client.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Total Paid */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <DollarSign size={20} className="text-green-600 mr-2" />
              <h3 className="font-medium text-gray-900">Total Paid</h3>
            </div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(totalPaid)}
            </div>
            <div className="text-sm text-gray-500">
              {totalTransactions} transactions
            </div>
          </div>

          {/* Outstanding Balance */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <Clock size={20} className="text-yellow-600 mr-2" />
              <h3 className="font-medium text-gray-900">Outstanding</h3>
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {formatCurrency(totalPending)}
            </div>
            <div className="text-sm text-gray-500">
              Pending payments
            </div>
          </div>

          {/* Saved Cards */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <CreditCard size={20} className="text-purple-600 mr-2" />
              <h3 className="font-medium text-gray-900">Payment Methods</h3>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {savedCards.length}
            </div>
            <div className="text-sm text-gray-500">
              Saved cards
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'overview', label: 'Overview', icon: Eye },
                { key: 'transactions', label: 'Transactions', icon: History },
                { key: 'cards', label: 'Payment Methods', icon: CreditCard }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center py-4 px-2 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Cleared Payments */}
                  <div>
                    <div className="flex items-center mb-4">
                      <CheckCircle size={20} className="text-green-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Recent Cleared Payments</h3>
                    </div>
                    <div className="space-y-3">
                      {getClearedPayments().slice(0, 5).length > 0 ? (
                        getClearedPayments().slice(0, 5).map((payment, index) => (
                          <div key={`cleared-${payment.transactionId}-${index}`} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center">
                              <CheckCircle size={16} className="text-green-600" />
                              <div className="ml-3">
                                <div className="font-medium text-gray-900">
                                  {formatCurrency(payment.amount)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {payment.type === 'immediate' ? 'Immediate Payment' : 'Scheduled Payment'} • {new Date(payment.date).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {payment.transactionId} • {payment.paymentType}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  Cleared
                                </span>
                                <button
                                  onClick={() => downloadReceipt(payment.transactionId, payment._id || payment.id)}
                                  className="text-blue-600 hover:text-blue-900 flex items-center"
                                  title="Download receipt"
                                >
                                  <Download size={14} className="mr-1" />
                                  <span className="text-xs">Receipt</span>
                                </button>
                              </div>
                              <div className="text-xs text-gray-500">
                                {payment.paymentMethod === 'cash' ? 'Cash' : payment.paymentMethod === 'cc_to_comp' ? 'CC to COMP' : `Card ****${payment.cardLast4 || '****'}`}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle size={48} className="mx-auto text-gray-300 mb-4" />
                          <p>No cleared payments yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scheduled Payments */}
                  <div>
                    <div className="flex items-center mb-4">
                      <Calendar size={20} className="text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Upcoming Scheduled Payments</h3>
                    </div>
                    <div className="space-y-3">
                      {getScheduledPayments().slice(0, 5).length > 0 ? (
                        getScheduledPayments().slice(0, 5).map((payment, index) => (
                          <div key={`scheduled-${payment.transactionId}-${index}`} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center">
                              <Calendar size={16} className="text-blue-600" />
                              <div className="ml-3">
                                <div className="font-medium text-gray-900">
                                  {formatCurrency(payment.amount)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Due: {new Date(payment.date).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {payment.transactionId} • {payment.paymentType}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center justify-end space-x-2 mb-2">
                                {/* Status badge */}
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  payment.status === 'declined' ? 'bg-red-100 text-red-800' :
                                  payment.status === 'failed' ? 'bg-orange-100 text-orange-800' :
                                  new Date(payment.date) < new Date() ? 'bg-yellow-100 text-yellow-800' : // Past due
                                  'bg-blue-100 text-blue-800' // Scheduled
                                }`}>
                                  {payment.status === 'declined' ? 'Declined' :
                                   payment.status === 'failed' ? 'Failed' :
                                   new Date(payment.date) < new Date() ? 'Past Due' :
                                   'Scheduled'}
                                </span>
                                
                                {/* Conditional buttons based on status */}
                                {(payment.status === 'declined' || payment.status === 'failed' || new Date(payment.date) < new Date()) ? (
                                  <>
                                    <button
                                      onClick={() => chargeNowScheduledPayment(payment.transactionId, payment._id || payment.id)}
                                      className="text-green-600 hover:text-green-900 flex items-center"
                                      title="Charge now"
                                    >
                                      <Zap size={14} className="mr-1" />
                                      <span className="text-xs">Charge Now</span>
                                    </button>
                                    <button
                                      onClick={() => reschedulePayment(payment.transactionId, payment._id || payment.id)}
                                      className="text-blue-600 hover:text-blue-900 flex items-center"
                                      title="Reschedule payment"
                                    >
                                      <CalendarClock size={14} className="mr-1" />
                                      <span className="text-xs">Reschedule</span>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => editScheduledPayment(payment.transactionId, payment._id || payment.id)}
                                      className="text-orange-600 hover:text-orange-900 flex items-center"
                                      title="Edit scheduled payment"
                                    >
                                      <Edit size={14} className="mr-1" />
                                      <span className="text-xs">Edit</span>
                                    </button>
                                  </>
                                )}
                                
                                {/* Delete button always available except for completed/processing */}
                                {!(['completed', 'processing'].includes(payment.status)) && (
                                  <button
                                    onClick={() => deleteScheduledPayment(payment.transactionId, payment._id || payment.id)}
                                    className="text-red-600 hover:text-red-900 flex items-center"
                                    title="Delete scheduled payment"
                                  >
                                    <Trash2 size={14} className="mr-1" />
                                    <span className="text-xs">Delete</span>
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {payment.paymentMethod === 'cash' ? 'Cash' : payment.paymentMethod === 'cc_to_comp' ? 'CC to COMP' : `Card ****${payment.cardLast4 || '****'}`}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
                          <p>No upcoming scheduled payments</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Methods Summary */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
                    <div className="space-y-3">
                      {savedCards.slice(0, 3).map((card) => (
                        <div key={card.cardId} className="flex items-center p-3 bg-gray-50 rounded-lg">
                          <CreditCard size={20} className="text-gray-600 mr-3" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {card.displayData?.cardType} •••• {card.displayData?.last4}
                            </div>
                            <div className="text-sm text-gray-500">
                              Expires {card.displayData?.expiryMonth}/{card.displayData?.expiryYear}
                            </div>
                          </div>
                        </div>
                      ))}
                      {savedCards.length === 0 && (
                        <div className="text-center py-4 text-gray-500">
                          No saved payment methods
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">All Transactions</h3>
                  <div className="text-sm text-gray-500">
                    {transactions.length} total transactions
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Transaction
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment Breakdown
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction) => (
                        <tr key={transaction.transactionId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">
                              {transaction.transactionId}
                            </div>
                            {transaction.policyNumber && (
                              <div className="text-sm text-gray-500">
                                Policy: {transaction.policyNumber}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {transaction.paymentType}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(transaction.amounts.total)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Paid: {formatCurrency(transaction.totalPaid || 0)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-4 text-xs">
                              {/* Cleared Payments */}
                              <div className="flex items-center">
                                <CheckCircle size={12} className="text-green-600 mr-1" />
                                <span className="text-green-700 font-medium">
                                  {formatCurrency(
                                    [...(transaction.chargeNow || []), ...(transaction.scheduledPayments || [])]
                                      .filter(p => p.status === 'completed')
                                      .reduce((sum, p) => sum + parseFloat(p.amount), 0)
                                  )}
                                </span>
                                <span className="text-gray-500 ml-1">cleared</span>
                              </div>
                              {/* Scheduled Payments */}
                              <div className="flex items-center">
                                <Calendar size={12} className="text-blue-600 mr-1" />
                                <span className="text-blue-700 font-medium">
                                  {formatCurrency(
                                    (transaction.scheduledPayments || [])
                                      .filter(p => p.status === 'scheduled' || p.status === 'processing')
                                      .reduce((sum, p) => sum + parseFloat(p.amount), 0)
                                  )}
                                </span>
                                <span className="text-gray-500 ml-1">scheduled</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                              {transaction.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(transaction.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              {/* Payment Plan Download - Only show if there are scheduled payments */}
                              {transaction.scheduledPayments && transaction.scheduledPayments.length > 0 && (
                                <button
                                  onClick={() => downloadPaymentPlan(transaction.transactionId)}
                                  className="text-indigo-600 hover:text-indigo-900 flex items-center"
                                  title="Download payment plan"
                                >
                                  <FileText size={14} className="mr-1" />
                                  <span className="text-xs">Plan</span>
                                </button>
                              )}
                              
                              {/* Individual Receipts for Completed Payments */}
                              {[...transaction.chargeNow, ...transaction.scheduledPayments]
                                .filter(payment => payment.status === 'completed')
                                .slice(0, 2) // Show max 2 receipt buttons
                                .map((payment, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => downloadReceipt(transaction.transactionId, payment._id)}
                                    className="text-blue-600 hover:text-blue-900 flex items-center"
                                    title={`Download receipt - ${formatCurrency(payment.amount)}`}
                                  >
                                    <Download size={14} className="mr-1" />
                                    <span className="text-xs">Receipt</span>
                                  </button>
                                ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {transactions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">No transactions found</p>
                    <p className="text-sm">This client hasn't made any payments yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* Cards Tab */}
            {activeTab === 'cards' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Saved Payment Methods</h3>
                  <div className="text-sm text-gray-500">
                    {savedCards.length} saved cards
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {savedCards.map((card) => (
                    <div key={card.cardId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <CreditCard size={24} className="text-gray-600" />
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          card.security?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {card.security?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="font-medium text-gray-900 mb-1">
                        {card.displayData?.cardType} •••• {card.displayData?.last4}
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        Expires {card.displayData?.expiryMonth}/{card.displayData?.expiryYear}
                      </div>
                      <div className="text-xs text-gray-500">
                        Used {card.security?.usageCount || 0} times
                      </div>
                      {card.security?.lastUsed && (
                        <div className="text-xs text-gray-500">
                          Last used: {formatDate(card.security.lastUsed)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {savedCards.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">No saved payment methods</p>
                    <p className="text-sm">This client hasn't saved any payment methods yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;